import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import Dialog from '/@/components/dialog';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import Input from '/@/v3/components/forms/input';
import styles from './styles.module.css';

/** @param {typeof propTypes} props */
function Geocoder(props) {
    const { country, query, trackEvent, onChange, onFocus, onBlur, onReset, onResult } = props;
    const [lastQuery, setlastQuery] = useState('');
    const [loading, setloading] = useState(false);
    const [showDialog, setshowDialog] = useState(false);

    const getPosition = () => {
        return new Promise((resolve) => {
            const positionFromString = getPositionFromString(query);
            if (positionFromString) {
                if (trackEvent) trackEvent({ key: 'fields.coordinates' });
                return resolve(positionFromString);
            }

            const geocoder = new google.maps.Geocoder();
            const options = { address: query, region: country };
            geocoder.geocode(options, (results, status) => {
                if (status === 'OK') {
                    if (trackEvent) trackEvent({ key: 'fields.geolocate', query });
                    const { location } = results?.[0]?.geometry;
                    resolve([location?.lng(), location?.lat()]);
                } else {
                    setshowDialog(true);
                    if (trackEvent) trackEvent({ key: 'fields.geolocateError' });
                    resolve();
                }
            });
        });
    };

    function getPositionFromString(query) {
        const list = query.split(',').map(Number);
        if (list.length !== 2) return;
        if (list.some(Number.isNaN)) return;
        // Google Maps and alike use (lat,lng) but we follow geojson spec which is (lng,lat).
        return list.reverse();
    }

    const run = async () => {
        const trimQuery = query.trim();
        if (trimQuery.length < 3 || trimQuery === lastQuery) return;
        setloading(true);
        setlastQuery(trimQuery);
        const position = await getPosition();
        if (position) onResult(position);
        setloading(false);
    };

    const runDebounced = useCallback(debounce(run, 2000), []);

    function handleDialogClose() {
        setshowDialog(false);
    }

    function handleKeyDown(event) {
        if (event.keyCode === 13) {
            event.target.blur();
            run();
        } else {
            runDebounced();
        }
    }

    return (
        <div className={styles.root}>
            <Dialog
                title={_('Error')}
                message={_('Ubicación no encontrada.')}
                visible={showDialog}
                onClose={handleDialogClose}
                onAccept={handleDialogClose}
            />
            <Input
                color={settings.app.theme}
                shadow
                placeholder={_('Buscar')}
                value={query}
                onChange={onChange}
                onKeyDown={handleKeyDown}
                onFocus={onFocus}
                onBlur={onBlur}
                onReset={onReset}
            />
            {loading && (
                <div className={styles.icon}>
                    <i className='material-icons'>gps_not_fixed</i>
                </div>
            )}
        </div>
    );
}

const propTypes = {
    query: PropTypes.string,
    country: PropTypes.string,
    trackEvent: PropTypes.func,
    onResult: PropTypes.func.isRequired,
    onFocus: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
};

Geocoder.propTypes = propTypes;

export default Geocoder;
